import Cookies from "js-cookie";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import environment from "src/environment";
import { useAxiosLazyQuery } from "src/hooks";
import { i18n } from "src/i18n";
import { convertNlToBr } from "src/services/string.service";
import { translate } from "src/services/translation.service";
import theme from "src/theme";
import styled from "styled-components";

import { Button, NewsletterSubscribe, Popin } from "..";

const StyledPopin = styled(Popin)`
  ${Popin.Container} {
    max-width: 500px;
  }

  ${Popin.Title} {
    font-family: ${theme.fonts.granville};
    font-size: ${theme.fontSizes.huge};
    margin-top: 0;
  }
`;

const Text = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.medium};
`;

const StyledNewsletterSubscribe = styled(NewsletterSubscribe)`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  margin: ${theme.margins.marginBase} 0;

  ${NewsletterSubscribe.EmailForm.Input} {
    color: #333333;
    border: solid 2px;
    border-right-width: 0;
    height: 40px;
  }

  ${NewsletterSubscribe.EmailForm.Button} {
    width: auto;
    margin: 0;
    background-color: #ffffff;
    color: #333333;
    border: solid 2px;
    border-left-width: 0;
    position: relative;
    font-weight: bold;
    padding: 1px 10px;
    letter-spacing: 1px;
    align-self: start;

    &:before {
      content: " ";
      position: absolute;
      top: 8px;
      bottom: 8px;
      left: -2px;
      border: 0 solid;
      border-left-width: 2px;
    }
  }
`;

const Subtext = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.small};
`;

const CGV = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.tiny};
  color: ${theme.colors.grey};
`;

const ContinueButton = styled(Button)`
  margin: ${theme.margins.marginBase} auto 0 auto;
  padding: 1px ${theme.margins.paddingPageBig};
`;

const COOKIE_NAME = "popin-optin";

interface IPopinOptinData {
  show: boolean;
  cookieLifetime: number;
  delay: number;
  ignoredPage: string[];

  popinActivated: boolean;
  offerActivated: boolean;
  offer: {
    title: string;
    text: string;
    image: string;
    subtext: string;
    cgv: string;
  };
  noOffer: {
    title: string;
    text: string;
    image: string;
    subtext: string;
    cgv: string;
  };

  isCustomer: boolean;
  customer: {
    title: string;
    image: string;
    text: string;
  };
  newCustomer: {
    title: string;
    image: string;
    text: string;
  };
}

enum Step {
  HIDE,
  CHECK_REGEX,
  NEWSLETTER_SUBSCRIPTION,
  COMPLETE,
  DONE,
}

export const PopinOptin = (): JSX.Element | null => {
  const [step, setStep] = useState<Step>(Step.HIDE);

  const url = environment.REACT_APP_API_ENDPOINT + "/popin-optin";
  const [query, { data }] = useAxiosLazyQuery<IPopinOptinData>({
    url,
    config: { params: { lang: i18n.language }, withCredentials: true },
  });

  /**
   * Get the cookie and start the query if it exists
   */
  const cookie = Cookies.get(COOKIE_NAME);
  useEffect(() => {
    if (!cookie) {
      query();
    }
  }, [cookie, query]);

  const location = useLocation();

  /**
   * Wait data.delay secondes before show popin,
   * check if the actual page is ignored by a regex.
   */
  const delay = useRef<number | null>(null);
  useEffect(() => {
    if (data?.show && delay.current === null) {
      delay.current = setTimeout(
        (): void => setStep(Step.CHECK_REGEX),
        data.delay * 1000
      );
    }
  }, [data, location.pathname]);

  /**
   * Check if the actual page is not an ignored page
   */
  useEffect(() => {
    if (data && step === Step.CHECK_REGEX) {
      const isIgnoredPage = data.ignoredPage.some((regex) =>
        new RegExp(regex).test(location.pathname)
      );

      setStep(isIgnoredPage ? Step.HIDE : Step.NEWSLETTER_SUBSCRIPTION);
    }
  }, [data, location.pathname, step]);

  /**
   * Set cookie for x days with a random value in it
   */
  const setCookie = useCallback(() => {
    if (data) {
      Cookies.set(COOKIE_NAME, Date.now() + "", {
        expires: data.cookieLifetime,
      });
    }
  }, [data]);

  /**
   * Go to done step and set cookie
   */
  const done = useCallback(() => {
    setStep(Step.DONE);
    setCookie();
  }, [setCookie]);

  /**
   * Check if the email belongs to a client who has already ordered (guest)
   */
  const [mailIsCustomer, setMailIsCustomer] = useState<boolean>(false);

  /**
   * Set cookie when user is on complete step
   */
  useEffect(() => {
    if (data && step === Step.COMPLETE) {
      setCookie();
    }
  }, [data, setCookie, step]);

  if (
    step === Step.HIDE ||
    step === Step.DONE ||
    !data ||
    !data.popinActivated
  ) {
    return null;
  }

  if (step === Step.NEWSLETTER_SUBSCRIPTION) {
    const { title, text, image, subtext, cgv } =
      data.offerActivated && !data.isCustomer ? data.offer : data.noOffer;

    return (
      <StyledPopin
        background="rgba(0, 0, 0, 0.3)"
        title={title}
        onClose={setCookie}
      >
        {data.offerActivated ? (
          <>
            <StyledNewsletterSubscribe
              buttonLabel="OK"
              onGuestSubscribe={({ guestNewsletterSubscribe }): void => {
                setStep(Step.COMPLETE);
                setMailIsCustomer(guestNewsletterSubscribe.isCustomer);
              }}
              onUserSubscribe={(): void => {
                setStep(Step.COMPLETE);
              }}
            />

            <Text>{convertNlToBr(text)}</Text>

            {image && <img src={image} alt="" />}

            <Subtext>{convertNlToBr(subtext)}</Subtext>
          </>
        ) : (
          <>
            <Text>{convertNlToBr(text)}</Text>

            <StyledNewsletterSubscribe
              buttonLabel="OK"
              onGuestSubscribe={({ guestNewsletterSubscribe }): void => {
                setStep(Step.COMPLETE);
                setMailIsCustomer(guestNewsletterSubscribe.isCustomer);
              }}
              onUserSubscribe={(): void => {
                setStep(Step.COMPLETE);
              }}
            />

            <Subtext>{convertNlToBr(subtext)}</Subtext>

            {image && <img src={image} alt="" />}
          </>
        )}
        <CGV>{convertNlToBr(cgv)}</CGV>
      </StyledPopin>
    );
  } else if (step === Step.COMPLETE) {
    const { title, text, image } =
      data.isCustomer || mailIsCustomer ? data.customer : data.newCustomer;

    return (
      <StyledPopin background="rgba(0, 0, 0, 0.3)" title={title} onClose={done}>
        {data.isCustomer ? (
          <>
            <Text>{convertNlToBr(text)}</Text>
            {image && <img src={image} alt="" />}
          </>
        ) : (
          <>
            {image && <img src={image} alt="" />}
            <Text>{convertNlToBr(text)}</Text>
          </>
        )}

        <ContinueButton title={translate("global.continue")} onClick={done} />
      </StyledPopin>
    );
  }

  return null;
};
